import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Switch } from "components/ui/switch";

import {
  FormikMultiSelect,
  toMultiSelectOptions,
} from "@/components/multi-select";
import { ProductDefinition } from "data/models";
import { formatProductDefinitionName } from "pages/geoscape-admin/manage-release/new-release/utils";
import { DateRangePicker } from "../share-component/data-range-picker";

export function DatasetFilters({
  values,
  handleChange,
  handleSubmit,
  definitions,
  time,
  setTime,
  setFieldValue,
  setQueryFilterState,
  resetForm,
  presetRange,
  setPresetRange,
  initialQueryFilterState,
  queryFilterState,
  localStorageKey,
}: any) {
  return (
    <div className="grid grid-cols-12 gap-4 mb-4">
      <div className="flex flex-col gap-1.5 w-full col-span-12 sm:col-span-6 lg:col-span-3">
        <Label htmlFor="query">Search</Label>
        <Input
          id="query"
          name="query"
          placeholder="Type to search... "
          type="text"
          value={values.query}
          onChange={(e: any) => {
            handleChange(e);
            handleSubmit();
          }}
        />
      </div>
      <div className="col-span-12 sm:col-span-6 lg:col-span-3">
        <FormikMultiSelect
          id="product"
          label="Product"
          options={
            definitions.isSuccess
              ? toMultiSelectOptions(
                  [...definitions.data.products].sort(
                    (a: ProductDefinition, b: ProductDefinition) =>
                      formatProductDefinitionName(
                        a.value,
                        a.displayName,
                      ).localeCompare(
                        formatProductDefinitionName(b.value, b.displayName),
                      ),
                  ),
                  // Labelled so Hub-only renames apply; the option value stays the product
                  // code, so the filter sent to the API is unchanged.
                  (s: ProductDefinition) =>
                    formatProductDefinitionName(s.value, s.displayName),
                )
              : []
          }
          fieldName="product"
          handleSubmit={handleSubmit}
          emptyLabel="All products"
        />
      </div>
      <div className="col-span-12 sm:col-span-6 lg:col-span-3">
        <FormikMultiSelect
          id="region"
          label="Region"
          options={
            definitions.isSuccess
              ? toMultiSelectOptions(definitions.data.regions)
              : []
          }
          fieldName="region"
          handleSubmit={handleSubmit}
          emptyLabel="All regions"
        />
      </div>
      <div className="col-span-12 sm:col-span-6 lg:col-span-3">
        <FormikMultiSelect
          id="format"
          label="Format"
          options={
            definitions.isSuccess
              ? toMultiSelectOptions(definitions.data.formats)
              : []
          }
          fieldName="format"
          handleSubmit={handleSubmit}
          emptyLabel="All formats"
        />
      </div>
      <div className="col-span-12 sm:col-span-6 lg:col-span-3">
        <FormikMultiSelect
          id="datum"
          label="Datum"
          options={
            definitions.isSuccess
              ? toMultiSelectOptions(definitions.data.datums)
              : []
          }
          fieldName="datum"
          handleSubmit={handleSubmit}
          emptyLabel="All datums"
        />
      </div>
      <div className="col-span-12 sm:col-span-6 lg:col-span-3">
        <DateRangePicker
          time={time}
          setTime={setTime}
          setFieldValue={setFieldValue}
          handleSubmit={handleSubmit}
          presetRange={presetRange}
          setPresetRange={setPresetRange}
          disabled={values.latestRelease}
          queryFilterState={queryFilterState}
        />
      </div>
      <div className="col-span-12 flex flex-row flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <Switch
            checked={values.latestRelease}
            onCheckedChange={(checked: boolean) => {
              const syntheticEvent = {
                target: {
                  name: "latestRelease",
                  value: checked,
                  type: "checkbox",
                  checked,
                },
              };
              handleChange(syntheticEvent);
              handleSubmit();
            }}
            id="latestRelease"
            name="latestRelease"
          />
          <Label htmlFor="latestRelease">Latest Release</Label>
        </div>
        <div className="flex items-center gap-2">
          <Switch
            checked={values.includeChildProducts}
            onCheckedChange={(checked: boolean) => {
              const syntheticEvent = {
                target: {
                  name: "includeChildProducts",
                  value: checked,
                  type: "checkbox",
                  checked,
                },
              };
              handleChange(syntheticEvent);
              handleSubmit();
            }}
            id="includeChildProducts"
            name="includeChildProducts"
          />
          <Label htmlFor="includeChildProducts">Insight Pack</Label>
        </div>
        <div className="flex items-center gap-2">
          <Switch
            checked={values.licenced}
            onCheckedChange={(checked: boolean) => {
              const syntheticEvent = {
                target: {
                  name: "licenced",
                  value: checked,
                  type: "checkbox",
                  checked,
                },
              };
              handleChange(syntheticEvent);
              handleSubmit();
            }}
            id="licenced"
            name="licenced"
          />
          <Label htmlFor="licenced">Licenced</Label>
          <Button
            variant="link"
            size="sm"
            id="clearFilters"
            name="clearFilters"
            onClick={() => {
              setTime({ startTime: 0, endTime: 0 });
              setFieldValue("release", "");
              localStorage.removeItem(localStorageKey);
              setQueryFilterState(initialQueryFilterState);
              resetForm();
              handleSubmit();
            }}
          >
            Clear
          </Button>
        </div>
      </div>
    </div>
  );
}
